import { useEffect, useState, type ReactNode } from 'react'

import { ChevronLeft, ChevronRight } from 'lucide-react'

import { IssuesGalleryGrid } from './IssuesGalleryGrid'
import { IssuesListHeaderRow } from './IssuesListHeaderRow'
import { IssuesListRow } from './IssuesListRow'
import { paginate } from '../../../lib/paginate'
import { ShowMoreFooter } from '../../ui/GroupedSections'
import { GroupMasterDetail } from '../../ui/GroupMasterDetail'
import { SegmentedControl } from '../../ui/SegmentedControl'

import type { GisIssue } from '../../../api/types'
import type { IssueGroup, IssueGroupBy } from '../../../lib/issue-groups'
import type { ResolutionTargets } from '../../../lib/resolution-targets'

const PER_PAGE_OPTIONS = [10, 25, 50] as const

/**
 * Progressive-reveal budget for the non-paginated presentations (flat gallery + grouped list/gallery):
 * mount at most this many rows on load, then reveal another `VISIBLE_STEP` per "Show more" click. Keeps
 * the default grouped Gallery from mounting the whole (up to 200-row) feed at once. The flat List keeps
 * its own footer pager instead.
 */
const INITIAL_VISIBLE = 30
const VISIBLE_STEP = 30

interface IssuesListBodyProps {
  view: 'list' | 'gallery'
  /** Active grouping dimension; 'none' renders the flat presentation. */
  groupBy: IssueGroupBy
  /** Pre-computed sections for `groupBy` (empty when 'none'). */
  groups: IssueGroup[]
  isPending: boolean
  isError: boolean
  onRetry: () => void
  rows: GisIssue[]
  hasAnyIssues: boolean
  targets: ResolutionTargets
  siteMandal: Map<string, string>
  /** Currently-open issue (drives row highlight); null when no detail is open. */
  selectedId: string | null
  /** Open an issue (detail surface in List view; centered modal in Gallery view). */
  onSelect: (id: string, siteId: string | null) => void
  onHover: (id: string | null) => void
}

const mandalOf = (i: GisIssue, siteMandal: Map<string, string>) =>
  i.siteId !== null ? (siteMandal.get(i.siteId) ?? null) : null

function Row({ i, p }: { i: GisIssue; p: IssuesListBodyProps }) {
  return (
    <IssuesListRow
      issue={i}
      targets={p.targets}
      mandalName={mandalOf(i, p.siteMandal)}
      isSelected={p.selectedId === i.id}
      onSelect={() => p.onSelect(i.id, i.siteId)}
      onLocate={null}
      onHover={p.onHover}
    />
  )
}

/** A gallery grid for one set of rows (shared by the flat and grouped Gallery presentations). */
function GalleryRows({ rows, p }: { rows: GisIssue[]; p: IssuesListBodyProps }) {
  const siteIdOf = (id: string) => rows.find((r) => r.id === id)?.siteId ?? null
  return (
    <IssuesGalleryGrid
      issues={rows}
      targets={p.targets}
      activeId={p.selectedId}
      onOpen={(id) => p.onSelect(id, siteIdOf(id))}
      onHover={p.onHover}
      siteMode={p.groupBy === 'question'}
    />
  )
}

/** Flat paginated list + footer pager (List view with groupBy === 'none'). */
function FlatList(p: IssuesListBodyProps & { page: number; perPage: number; setPage: (n: number) => void; setPerPage: (n: number) => void }) {
  const pg = paginate(p.rows, p.page, p.perPage)
  return (
    <>
      <div className="@container/list min-h-0 flex-1 overflow-y-auto p-2">
        <IssuesListHeaderRow />
        <div className="space-y-1 pt-1">
          {pg.items.map((i) => (
            <Row key={i.id} i={i} p={p} />
          ))}
        </div>
      </div>

      <footer className="flex items-center gap-3 border-t border-gis-line-2 px-3 py-[8px] text-[11.5px] text-gis-soft">
        <span>Showing {pg.from} to {pg.to} of {pg.total} issues</span>
        <div className="ml-auto flex items-center gap-1">
          <button type="button" onClick={() => p.setPage(pg.page - 1)} disabled={pg.page <= 1} aria-label="Previous page" className="grid h-[26px] w-[26px] place-items-center rounded-md text-gis-soft transition-colors hover:bg-gis-track active:brightness-95 disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-blue-500/60">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="px-1 text-[11.5px] font-semibold tabular-nums text-gis-ink">{pg.page}</span>
          <span className="text-gis-muted">/ {pg.pageCount}</span>
          <button type="button" onClick={() => p.setPage(pg.page + 1)} disabled={pg.page >= pg.pageCount} aria-label="Next page" className="grid h-[26px] w-[26px] place-items-center rounded-md text-gis-soft transition-colors hover:bg-gis-track active:brightness-95 disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-blue-500/60">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        {/* One-click page-size switch — the shared segmented control so the footer matches the
            rest of the panel's controls. */}
        <span className="flex items-center gap-1.5">
          Rows
          <SegmentedControl<'10' | '25' | '50'>
            aria-label="Rows per page"
            size="xs"
            track="b"
            shape="rounded"
            items={PER_PAGE_OPTIONS.map((n) => ({ key: String(n) as '10' | '25' | '50', label: String(n) }))}
            value={String(p.perPage) as '10' | '25' | '50'}
            onChange={(k) => p.setPerPage(Number(k))}
          />
        </span>
      </footer>
    </>
  )
}

/** The scroll region every non-paginated presentation shares. */
function ScrollRegion({ children }: { children: ReactNode }) {
  return <div className="@container/list min-h-0 flex-1 overflow-y-auto p-2">{children}</div>
}

export function IssuesListBody(p: IssuesListBodyProps) {
  const [perPage, setPerPage] = useState<number>(25)
  const [page, setPage] = useState(1)
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE)
  const { view, groupBy, isPending, isError, onRetry, rows, hasAnyIssues } = p
  const grouped = groupBy !== 'none'
  const showMore = (): void => setVisibleCount((v) => v + VISIBLE_STEP)

  // Reset to page 1 whenever the working set, view, or page size changes. Selecting a row does NOT
  // change `rows`, so opening the detail never jumps the list off the current page.
  useEffect(() => {
    setPage(1)
  }, [rows, perPage, view])

  // Reset the progressive-reveal budget when the working set, view, or grouping changes. Opening a
  // detail doesn't change `rows`, so the reveal survives Prev/Next navigation.
  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE)
  }, [rows, view, groupBy])

  if (isPending) {
    return (
      <div className="space-y-2 p-3" aria-hidden>
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="h-[50px] animate-pulse rounded-[12px] bg-gis-track" />
        ))}
      </div>
    )
  }
  if (isError) {
    return (
      <div className="text-gis-soft p-6 text-center text-[12px]">
        Couldn't load issues.{' '}
        <button type="button" onClick={onRetry} className="text-gis-navy font-semibold underline">Retry</button>
      </div>
    )
  }
  if (rows.length === 0) {
    return (
      <div className="text-gis-soft p-6 text-center text-[12px]">
        {hasAnyIssues ? 'No issues match these filters.' : 'No issues recorded for this selection.'}
      </div>
    )
  }

  if (view === 'gallery') {
    // Grouped → master-detail split (group titles left, the selected group's image boxes right);
    // flat → the single wrapping grid.
    return grouped ? (
      <GroupMasterDetail groups={p.groups} renderGroup={(groupRows) => <GalleryRows rows={groupRows} p={p} />} />
    ) : (
      <ScrollRegion>
        {/* Flat gallery keeps the progressive-reveal budget (never mount the whole 200-row feed);
            grouped gallery is the master-detail split above, which mounts one group at a time. */}
        <div className="pt-1"><GalleryRows rows={rows.slice(0, visibleCount)} p={p} /></div>
        <ShowMoreFooter shown={Math.min(visibleCount, rows.length)} total={rows.length} onMore={showMore} />
      </ScrollRegion>
    )
  }

  if (grouped) {
    // List view, grouped → the same split, with the group's rows in the right pane.
    return (
      <GroupMasterDetail
        groups={p.groups}
        renderGroup={(groupRows) => (
          <div className="space-y-1">
            {groupRows.map((i) => (
              <Row key={i.id} i={i} p={p} />
            ))}
          </div>
        )}
      />
    )
  }

  return <FlatList {...p} page={page} perPage={perPage} setPage={setPage} setPerPage={setPerPage} />
}
